import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usersApi } from '@/lib/users/users.api';
import { Modal } from '@symphenia/ui-components';
import { tenantModulesApi } from '@/lib/modules/tenant-modules.api';
import { moduleLabel } from '@/lib/module-label';
import { useAuthStore } from '@/lib/auth.store';
import { erfuelltRecht } from '@symphenia/shared-types';
import { rolesApi, type RoleView } from '@/lib/roles/roles.api';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const LOCALES = ['de', 'en'];

/**
 * AP-29-N4 — Ein Topf mit Plätzen, wie ihn das Formular anbietet.
 *
 * Plätze gelten je **Gruppe**, nicht je Baustein (AP-29-N): Sechs gebuchte
 * Teile der Zeiterfassung ergeben hier eine Zeile, nicht sechs.
 */
interface PlatzTopf {
  key: string;
  label: string;
  frei: number;
  gekauft: number;
}

export function InviteUserDialog({ open, onClose, onSuccess }: Props) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    email: '',
    // AP-30 Stufe 3: der Schlüssel einer Rolle dieses Mandanten, nicht ein Rang.
    roleKey: 'employee',
    preferredLocale: 'de',
    firstName: '',
    lastName: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toepfe, setToepfe] = useState<PlatzTopf[]>([]);
  const [moduleKeys, setModuleKeys] = useState<string[]>([]);
  // AP-29-N4 / AP-30-D: Modul-Häkchen im Einladungsformular sind
  // Platzvergabe — dasselbe Recht wie auf `/module-seats`. Ohne diese
  // Prüfung wäre dieses Formular der eine Weg, auf dem jemand ohne das
  // Recht Modulzugang verteilt.
  const rechte = useAuthStore((s) => s.user?.permissions);

  /**
   * Die Rollen dieses Mandanten — eigene inbegriffen.
   *
   * Vorher stand hier eine Liste von vier Rollennamen im Code. Ein Betrieb, der
   * „Schriftverkehr: Lieferscheine" angelegt hat, konnte sie beim Einladen nicht
   * vergeben und musste den Benutzer hinterher in der Rollenverwaltung
   * nachbessern.
   */
  const [rollen, setRollen] = useState<RoleView[]>([]);
  useEffect(() => {
    if (!open) return;
    rolesApi.list().then(setRollen).catch(() => undefined);
  }, [open]);
  const istAdmin = erfuelltRecht('core.seat.manage', rechte);

  // AP-29-N4: Angeboten wird nur, was der Betrieb **gebucht** hat und was
  // überhaupt Plätze kennt — bei „alle Benutzer" gibt es nichts zuzuteilen.
  // Der Endpunkt ist ADMIN-only; ein MANAGER lädt ihn nicht und bekommt die
  // Häkchen gar nicht erst zu sehen (Plätze vergibt ohnehin nur ein ADMIN).
  useEffect(() => {
    if (!open || !istAdmin) return;
    let abgebrochen = false;
    tenantModulesApi
      .getModules()
      .then((res) => {
        if (abgebrochen) return;
        const jeTopf = new Map<string, PlatzTopf>();
        for (const eintrag of res.data.catalog ?? []) {
          if (!eintrag.bookedForTenant || !eintrag.booking) continue;
          if (eintrag.booking.seatMode !== 'ASSIGNED' || eintrag.booking.seats === null) continue;
          const topf = eintrag.parent ?? eintrag.key;
          if (jeTopf.has(topf)) continue;
          jeTopf.set(topf, {
            key: topf,
            label: moduleLabel(topf, t),
            frei: Math.max(0, eintrag.booking.seats - eintrag.booking.seatsUsed),
            gekauft: eintrag.booking.seats,
          });
        }
        setToepfe([...jeTopf.values()]);
      })
      .catch(() => undefined);
    return () => {
      abgebrochen = true;
    };
  }, [open, istAdmin, t]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await usersApi.invite({ ...form, ...(moduleKeys.length && { moduleKeys }) });
      onSuccess();
      onClose();
      setForm({ email: '', roleKey: 'employee', preferredLocale: 'de', firstName: '', lastName: '' });
      setModuleKeys([]);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('common.error');
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="md" className="max-w-md" hideCloseButton>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">{t('users.invite')}</h2>
          <button
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t('users.firstName')}
              </label>
              <input
                type="text"
                value={form.firstName}
                onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t('users.lastName')}
              </label>
              <input
                type="text"
                value={form.lastName}
                onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              {t('auth.email')} *
            </label>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="name@firma.com"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t('users.role')} *
              </label>
              <select
                value={form.roleKey}
                onChange={(e) => setForm((f) => ({ ...f, roleKey: e.target.value }))}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {rollen.map((r) => (
                  <option key={r.key} value={r.key}>
                    {r.isSystem
                      ? t(`roles.templates.${r.templateKey}.name`, { defaultValue: r.name })
                      : r.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t('users.preferredLocale')}
              </label>
              <select
                value={form.preferredLocale}
                onChange={(e) => setForm((f) => ({ ...f, preferredLocale: e.target.value }))}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {LOCALES.map((l) => (
                  <option key={l} value={l}>
                    {l === 'de' ? 'Deutsch' : 'English'}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {toepfe.length > 0 && (
            <div className="space-y-2 rounded-md border border-border p-3">
              <p className="text-sm font-medium text-foreground">{t('users.moduleAccess')}</p>
              {toepfe.map((topf) => (
                <label key={topf.key} className="flex cursor-pointer items-center gap-3">
                  <input
                    type="checkbox"
                    checked={moduleKeys.includes(topf.key)}
                    onChange={(e) =>
                      setModuleKeys((k) =>
                        e.target.checked ? [...k, topf.key] : k.filter((x) => x !== topf.key),
                      )
                    }
                    className="h-4 w-4 rounded border-border text-primary focus:ring-2 focus:ring-ring"
                  />
                  <span className="text-sm text-foreground">{topf.label}</span>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {topf.frei > 0
                      ? t('users.seatsFree', { free: topf.frei, total: topf.gekauft })
                      : t('users.seatsNoneFree')}
                  </span>
                </label>
              ))}
              {/* Die Zahl ist eine Auskunft, kein Versprechen: Zwischen Einladung
                  und Annahme kann sich beides ändern. */}
              <p className="text-xs text-muted-foreground">{t('users.moduleAccessHint')}</p>
            </div>
          )}

          {error && (
            <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-input px-4 py-2 text-sm font-medium text-foreground hover:bg-muted/50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {loading ? t('common.loading') : t('users.invite')}
            </button>
          </div>
        </form>
    </Modal>
  );
}
